import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiBell, FiMenu, FiLogOut, FiUser, FiChevronDown, FiCompass, FiBookOpen } from "react-icons/fi";
import { startProductTour } from "../components/tour/ProductTour";
import { toast } from "react-toastify";
import { useRole } from "../context/RoleContext";
import { useCycle } from "../context/CycleContext";
import { ROLE_LABELS } from "../services/navigation";
import { markNotificationRead, markAllNotificationsRead } from "../services/notificationApi";
import Avatar from "../components/common/Avatar";
import StyleMenu from "./StyleMenu";
import { timeAgo } from "../utils/format";
import Select from "../components/common/Select"; // [NEW] alphabetical dropdown (replaces <select>)

const api = { markNotificationRead, markAllNotificationsRead };

function useOutsideClose(ref, onClose) {
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, onClose]);
}

export default function Topbar({ onMenu, notifications = [], onNotificationsChange }) {
  const { profile, logout, counters } = useRole();
  const { cycles, cycle, setCycleId } = useCycle();
  const navigate = useNavigate();
  const [bellOpen, setBellOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const bellRef = useRef(null);
  const menuRef = useRef(null);
  useOutsideClose(bellRef, () => setBellOpen(false));
  useOutsideClose(menuRef, () => setMenuOpen(false));

  const openNotification = async (n) => {
    setBellOpen(false);
    if (!n.read) {
      await api.markNotificationRead(n.id);
      onNotificationsChange?.();
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.markAllNotificationsRead();
    onNotificationsChange?.();
  };

  const signOut = () => {
    logout();
    toast.info("You have been logged out.");
    navigate("/login", { replace: true });
  };

  return (
    <header className="topbar">
      <button type="button" className="icon-btn topbar-menu" onClick={onMenu} aria-label="Open menu">
        <FiMenu size={20} />
      </button>

      <label className="cycle-select">
        <span>Cycle</span>
        <Select value={cycle?.id || ""} onChange={(e) => setCycleId(e.target.value)}>
          {cycles.map((c) => (
            <option key={c.id} value={c.id}>{c.name}{c.status === "Active" ? " (active)" : c.status === "Draft" ? " (draft)" : ""}</option>
          ))}
        </Select>
      </label>

      <div className="topbar-spacer" />

      {/* [UPDATED] PM-19: the "View as" switch is removed; every menu is in the sidebar. */}

      <StyleMenu />

      <div className="topbar-pop" ref={bellRef}>
        <button type="button" className="icon-btn bell" onClick={() => setBellOpen((v) => !v)} aria-label={`Notifications, ${counters.unread} unread`} aria-expanded={bellOpen}>
          <FiBell size={19} />
          {counters.unread > 0 && <span className="bell-count">{counters.unread > 9 ? "9+" : counters.unread}</span>}
        </button>
        {bellOpen && (
          <div className="popover popover-wide">
            <div className="popover-head">
              <strong>Notifications</strong>
              {counters.unread > 0 && <button type="button" className="link-btn" onClick={markAll}>Mark all as read</button>}
            </div>
            {notifications.length === 0 ? (
              <p className="popover-empty">You're all caught up.</p>
            ) : (
              <ul className="notif-list">
                {notifications.slice(0, 5).map((n) => (
                  <li key={n.id}>
                    <button type="button" className={`notif-item ${n.read ? "" : "is-unread"}`} onClick={() => openNotification(n)}>
                      <span className="notif-title">{n.title}</span>
                      <span className="notif-msg">{n.message}</span>
                      <span className="notif-time">{timeAgo(n.at)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/notifications" className="popover-foot" onClick={() => setBellOpen(false)}>See all notifications</Link>
          </div>
        )}
      </div>

      <div className="topbar-pop" ref={menuRef}>
        <button type="button" className="user-chip" onClick={() => setMenuOpen((v) => !v)} aria-expanded={menuOpen}>
          <Avatar name={profile?.name} size={34} />
          <span className="user-chip-text">
            <span className="user-chip-name">{profile?.name}</span>
            <span className="user-chip-role">{ROLE_LABELS[profile?.role] || "Employee"}</span>
          </span>
          <FiChevronDown size={15} aria-hidden="true" />
        </button>
        {menuOpen && (
          <div className="popover">
            <div className="popover-user">
              <strong>{profile?.name}</strong>
              <span>{profile?.email || profile?.username}</span>
            </div>
            <Link to="/profile" className="menu-item" onClick={() => setMenuOpen(false)}>
              <FiUser size={15} /> My profile
            </Link>
            <Link to="/help/user-manual" className="menu-item" onClick={() => setMenuOpen(false)}>
              <FiBookOpen size={15} /> User manual
            </Link>
            <button type="button" className="menu-item" onClick={() => { setMenuOpen(false); startProductTour(); }}>
              <FiCompass size={15} /> Take the tour
            </button>
            <button type="button" className="menu-item is-danger" onClick={signOut}>
              <FiLogOut size={15} /> Log out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
